import { Call, Device } from '@twilio/voice-sdk';
import { Sentry } from 'lib-shared-sentry';

import { withTimeout } from 'utils/withTimeout';

import { makeTwilioDeviceOptions } from './deviceOptions';
import { shouldDestroyDeviceOnError } from './errorCodes';
import type {
  CallId,
  CallWaitingType,
  QueuedCall,
  TokenResponse,
  TwilioEventNotifiers,
} from './types';

export type CallResult = {
  status: string;
  direction: string;
  csid: string | null;
  // String, not boolean — Twilio passes custom call params as raw strings over
  // the wire (e.g. muted: 'true'). Consumers are responsible for parsing.
  muted: string | undefined;
};

export type DisconnectResult = {
  status: string;
};

export type MakeCallParams = {
  userId: string;
  fromNumber: string;
  toNumber: string;
};

export type ConnectCallParams = {
  csid: CallId;
  muted?: boolean;
  isOnlyListening?: boolean;
};

export type FeedbackParams = {
  score: Call.FeedbackScore;
  issue: Call.FeedbackIssue | null;
};

type InitiateCallParams = {
  type: CallWaitingType;
  params: Record<string, string>;
};

/**
 * Owns all mutable Twilio runtime state and communicates state changes back
 * through TwilioEventHandlers. Has no knowledge of Redux or token regions — it
 * only knows about the Twilio SDK, token management, and call lifecycle.
 *
 * The region is encoded in the token itself; the middleware controls which
 * region's token is returned by the getToken callback.
 *
 * Command methods return promises that resolve when the Twilio SDK confirms the
 * operation, bridging Twilio's event-based model to a promise-based one.
 */
export class TwilioRuntime {
  // ─── Private mutable state ─────────────────────────────────────────────────

  private device: Device | null = null;

  // Monotonically increasing counter used as a cancellation token for
  // initializeDevice(). Each call captures the current value; if a newer
  // init starts (or close() is called) before createDevice() resolves,
  // the captured value will be stale and the result is discarded.
  private initializeSequence = 0;

  private isOffline = true;
  private isShutdown = false;
  private isFetchingToken = false;
  private queuedCall: QueuedCall = null;

  /** Cancels a pending {@link awaitRegisteredOrStale} from a superseded init. */
  private endAwaitRegistered: (() => void) | null = null;

  // ─── Stable dependencies ────────────────────────────────────────────────────

  private readonly getToken: () => Promise<TokenResponse>;
  private readonly notifiers: TwilioEventNotifiers;

  constructor(
    getToken: () => Promise<TokenResponse>,
    notifiers: TwilioEventNotifiers,
  ) {
    this.getToken = getToken;
    this.notifiers = notifiers;
  }

  /**
   * Runs a notifier callback in isolation so a dispatch/reducer error inside
   * the notifier cannot escape through a Twilio SDK event callback or prevent
   * runtime cleanup from completing.
   */
  private safeNotify(fn: () => void): void {
    try {
      fn();
    } catch (error) {
      Sentry.captureException(error);
    }
  }

  // ─── Device initialization ─────────────────────────────────────────────────

  // Tears down any existing device, fetches a fresh token, and creates a new
  // Device. Safe to call multiple times — the initializeSequence token ensures
  // only the most recent call's result is kept.
  async initializeDevice(): Promise<void> {
    const sequence = ++this.initializeSequence;
    this.endAwaitRegistered?.();
    this.endAwaitRegistered = null;

    const previousDevice = this.device;

    this.device = null;
    previousDevice?.destroy();

    const device = await this.createDevice();

    if (this.isShutdown || sequence !== this.initializeSequence) {
      device.destroy();
      return;
    }

    this.device = device;
    this.attachDeviceEventHandlers(device);

    // Register after handlers are attached so `registered` is never missed.
    // Twilio docs recommend registering in response to a user interaction to
    // ensure audio works across browsers.
    device.register();

    // Wait until this device reports registered (or init is superseded) so
    // callers do not observe isOffline=true due to microtask ordering.
    await this.awaitRegisteredOrStale(device, sequence);
  }

  // Fetches a token and constructs a new Device. Does NOT assign it to
  // this.device — the caller decides whether to keep or discard it.
  private async createDevice(): Promise<Device> {
    const { data } = await this.getToken();
    return new Device(data.token, makeTwilioDeviceOptions(data.ice_servers));
  }

  // Wires SDK events to runtime methods. Incoming calls are auto-accepted;
  // listeners use `once` to avoid duplicate firings across reconnects.
  private attachDeviceEventHandlers(device: Device): void {
    device.on('registered', () => this.onDeviceRegistered(device));
    device.on('unregistered', () => this.onDeviceUnregistered(device));
    device.on('error', (error) => this.onDeviceError(device, error));
    device.on('tokenWillExpire', () => this.onDeviceTokenWillExpire(device));
    device.on('incoming', (call) => this.onDeviceIncomingCall(call));
    device.on('cancel', () => this.onDeviceCancel());
  }

  // Destroys the device for fatal errors (timeout, expired JWT) and clears
  // the reference so subsequent commands know the device is gone.
  private onDeviceError(
    device: Device,
    error: { code: number; message: string },
  ): void {
    if (shouldDestroyDeviceOnError(error.code)) {
      device.destroy();
      if (this.device === device) {
        this.device = null;
      }
    }
    this.safeNotify(() => this.notifiers.onDeviceError(error));
  }

  // Notifies the middleware, attaches call-level lifecycle listeners, then
  // auto-accepts. Listeners use `once` because each call only connects
  // and disconnects once.
  private onDeviceIncomingCall(call: Call): void {
    this.safeNotify(() =>
      this.notifiers.onIncomingCall({
        from: call.parameters.From,
        to: call.parameters.To,
      }),
    );

    call.once('accept', () => this.onCallConnected(call));
    call.once('disconnect', () => this.onCallDisconnected(call));
    call.accept();
  }

  // Fired ~10 seconds before the token expires. Proactively refreshes
  // the token so the device stays registered without interruption.
  private onDeviceTokenWillExpire(device: Device): void {
    this.refreshDeviceToken(device);
  }

  // The caller hung up before the agent could answer.
  private onDeviceCancel(): void {
    this.safeNotify(() => this.notifiers.onCallMissed());
  }

  // Fetches a fresh token and updates the device.
  // Silently discards the result if the device was replaced or shutdown
  // during the async gap.
  private async refreshDeviceToken(device: Device): Promise<void> {
    if (this.isShutdown || this.device !== device || this.isFetchingToken) {
      return;
    }

    this.isFetchingToken = true;

    try {
      const response = await this.getToken();

      if (this.isShutdown || this.device !== device) {
        return;
      }

      device.updateToken(response.data.token);
    } catch (exception) {
      console.error('Twilio get token exception', exception);

      if (this.queuedCall !== null) {
        const failedCallType = this.queuedCall.type;
        console.error(
          'Twilio: aborting queued call due to token refresh failure.',
          failedCallType,
        );
        this.clearQueuedCall(
          new Error('Twilio: token refresh failed while call was queued.'),
        );
      }
    } finally {
      this.isFetchingToken = false;
    }
  }

  // ─── Device event handlers ─────────────────────────────────────────────────

  // Device is online and ready. Replays any call that was queued while offline.
  private onDeviceRegistered(device: Device): void {
    if (this.device !== device) {
      return;
    }

    Sentry.addBreadcrumb({
      category: 'twilio',
      message: 'Twilio HandleOnRegistered: onRegistered',
      level: 'info',
      data: this.breadcrumbData(),
    });

    this.isOffline = false;

    if (this.device !== null) {
      this.safeNotify(() => this.notifiers.onDeviceStatusChanged(this.device!));
    }

    this.resumeQueuedCallIfExists();
  }

  // Device went offline. Token refresh is handled proactively by
  // tokenWillExpire, so this only updates state and notifies.
  private onDeviceUnregistered(device: Device): void {
    if (this.device !== device) {
      return;
    }

    Sentry.addBreadcrumb({
      category: 'twilio',
      message: 'Twilio HandleOnUnregistered: onUnregistered',
      level: 'info',
      data: this.breadcrumbData(),
    });

    this.isOffline = true;

    if (this.device !== null) {
      this.safeNotify(() => this.notifiers.onDeviceStatusChanged(this.device!));
    }
  }

  // Fired when the Twilio SDK confirms media is flowing (the 'accept' event).
  // Sets the global currentPhoneCall and notifies the outside world.
  private onCallConnected(call: Call): void {
    window.currentPhoneCall = call;

    const params = Object.fromEntries(call.customParameters);
    const csid = params.conference ?? params.callback ?? null;
    const muted = params.muted;

    // INVARIANT: notify *after* assigning window.currentPhoneCall above.
    // PhoneForm reads window.currentPhoneCall when Redux notifies it about a
    // call lifecycle change. If dispatch lands before the global is set,
    // PhoneForm binds to the old or null Call and the audio meter freezes
    // silently. Do not reorder.
    this.safeNotify(() => this.notifiers.onCallEstablished({ csid, muted }));
  }

  // Fires for both local and remote hangups. Clears the global connection
  // reference so subsequent commands know there is no active call.
  private onCallDisconnected(call: Call): void {
    window.currentPhoneCall = null;

    const params = Object.fromEntries(call.customParameters ?? new Map());
    // INVARIANT: notify *after* clearing window.currentPhoneCall above. See
    // onCallConnected for the full rationale — same ordering contract on the
    // teardown side so PhoneForm unbinds against the right Call instance.
    this.safeNotify(() => this.notifiers.onCallTerminated({ message: params }));
  }

  // ─── Public command interface ──────────────────────────────────────────────

  // Dials an external phone number. Resolves when the remote party answers.
  async makeCall({
    userId,
    fromNumber,
    toNumber,
  }: MakeCallParams): Promise<CallResult> {
    return this.initiateCall({
      type: 'outboundCall',
      params: {
        outbound: userId,
        from: fromNumber,
        dixaOutboundToPn: toNumber,
      },
    });
  }

  // Initiates a callback to a customer. The csid can be a number or string.
  async makeCallback(csid: CallId): Promise<CallResult> {
    return this.initiateCall({
      type: 'callbackCall',
      params: { callback: String(csid) },
    });
  }

  // Calls another agent within the organization.
  async makeInternalCall(userId: string): Promise<CallResult> {
    return this.initiateCall({
      type: 'internalCall',
      params: { internal: userId },
    });
  }

  // Joins an existing conference (e.g. reconnecting to a conversation).
  // Muted/isOnlyListening are converted to string params for the backend.
  async connectCall({
    csid,
    muted,
    isOnlyListening,
  }: ConnectCallParams): Promise<CallResult> {
    const params: Record<string, string> = {
      conference: String(csid),
      isOnlyListening: isOnlyListening ? 'true' : 'false',
    };

    if (muted === true) {
      // The backend expects that muted is a boolean string.
      params.muted = 'true';
    }

    return this.initiateCall({
      type: 'conferenceJoin',
      params,
    });
  }

  // Hangs up the active call. Registers the disconnect listener before
  // calling disconnect() to avoid a race with synchronous event delivery.
  disconnectCall(): Promise<DisconnectResult> {
    if (this.isOffline || window.currentPhoneCall === null) {
      return Promise.reject(
        new Error('Twilio: DisconnectCall is offline or no active call.'),
      );
    }

    const connection = window.currentPhoneCall;
    const { promise, resolve } = Promise.withResolvers<DisconnectResult>();

    connection.once('disconnect', () => {
      resolve({ status: connection.status() });
    });

    connection.disconnect();

    return withTimeout(promise, 60_000, 'Twilio: disconnect timed out.');
  }

  // Sends DTMF tones on the active call (e.g. navigating phone menus).
  sendDigits(digit: string): void {
    if (!window.currentPhoneCall) {
      return;
    }

    window.currentPhoneCall.sendDigits(digit);
  }

  // Submits post-call quality feedback. Returns the SDK's response directly.
  sendFeedback({ score, issue }: FeedbackParams): Promise<unknown> {
    if (window.currentPhoneCall === null) {
      return Promise.reject(new Error('No current Twilio call connection.'));
    }

    return window.currentPhoneCall.postFeedback(score, issue ?? undefined);
  }

  // Permanently tears down the runtime. Bumps initializeSequence so any
  // in-flight initializeDevice() discards its result on completion.
  shutdown(): void {
    this.isShutdown = true;
    this.initializeSequence += 1;
    this.endAwaitRegistered?.();
    this.endAwaitRegistered = null;
    this.device?.destroy();
    this.device = null;
  }

  // ─── Internal helpers ──────────────────────────────────────────────────────

  /**
   * Resolves after the first `registered` event for this device instance, or
   * immediately if initialization was superseded.
   *
   * Also escapes on `unregistered`, `error`, or a hard timeout so a stuck SDK
   * can never block call setup indefinitely. When the wait resolves without
   * `registered`, `isOffline` remains `true` and `initiateCall` queues the call
   * normally.
   */
  private async awaitRegisteredOrStale(
    device: Device,
    sequence: number,
  ): Promise<void> {
    await new Promise<void>((resolve) => {
      if (this.isShutdown || sequence !== this.initializeSequence) {
        resolve();
        return;
      }

      const cleanup = () => {
        device.off('registered', onEvent);
        device.off('unregistered', onEvent);
        device.off('error', onEvent);
        clearTimeout(timer);
        if (this.endAwaitRegistered === cancel) {
          this.endAwaitRegistered = null;
        }
        resolve();
      };

      const onEvent = () => cleanup();
      const cancel = () => cleanup();

      const timer = setTimeout(() => cleanup(), 10_000);

      this.endAwaitRegistered = cancel;
      device.on('registered', onEvent);
      device.on('unregistered', onEvent);
      device.on('error', onEvent);
    });
  }

  /**
   * Initiates a call via device.connect() and returns a promise that resolves
   * when Twilio confirms the call is accepted. If the device is offline, the
   * call is queued and replayed once the device comes back online.
   */
  private async initiateCall({
    type,
    params,
  }: InitiateCallParams): Promise<CallResult> {
    // Internal calls bypass ensureDeviceRegion so they reach here without a
    // currentRegion. That is intentional. The lazy init below uses whatever region was last set (or none).
    if (this.device === null && !this.isShutdown) {
      await this.initializeDevice();
    }

    // If the device is still null after initialization (e.g. it was destroyed
    // by a fatal error like an invalid token), fail fast rather than queuing.
    // Queuing would hang the call promise forever because the device can never
    // come back online on its own in this state.
    if (this.device === null) {
      throw new Error('Twilio: Device is unavailable.');
    }

    if (this.isOffline) {
      return this.queueCallUntilOnline({ type, params });
    }

    const call = await this.device.connect({ params });
    const { promise, resolve, reject } = Promise.withResolvers<CallResult>();

    call.once('disconnect', () => {
      this.onCallDisconnected(call);
      reject(new Error('Twilio: Call disconnected before being accepted.'));
    });

    call.once('accept', () => {
      this.onCallConnected(call);

      const callParams = Object.fromEntries(call.customParameters);
      resolve({
        status: call.status(),
        direction: call.direction,
        csid: callParams.conference ?? callParams.callback ?? null,
        muted: callParams.muted,
      });
    });

    return withTimeout(promise, 60_000, 'Twilio: call accept timed out.');
  }

  // Stores the call request and re-initializes the device. The returned
  // promise settles when the replay eventually connects (or fails).
  // If initializeDevice itself fails (e.g. token fetch error), the queued
  // call is rejected so the saga promise doesn't hang forever.
  private queueCallUntilOnline({
    type,
    params,
  }: InitiateCallParams): Promise<CallResult> {
    const { promise, resolve, reject } = Promise.withResolvers<CallResult>();

    /**
     * Queued calls get picked up by the device when it comes online again. @see
     * onDeviceRegistered()
     */
    this.queuedCall = {
      type,
      replay: () => this.initiateCall({ type, params }).then(resolve, reject),
      abort: reject,
    };

    this.initializeDevice().catch(() => {
      this.clearQueuedCall(
        new Error('Twilio: device failed to initialize while offline.'),
      );
    });

    return promise;
  }

  // Replays the queued call now that the device is online. Clears the
  // queue first to avoid re-entrancy if replay itself queues again.
  private resumeQueuedCallIfExists(): void {
    if (this.queuedCall === null) return;

    const { replay } = this.queuedCall;
    this.queuedCall = null;
    replay();
  }

  // Discards the queued call and rejects its promise if an error is provided.
  // Always call with an error when the failure makes it impossible for the call
  // to ever be replayed; omit the error only when the caller is responsible for
  // settling the promise separately.
  private clearQueuedCall(error?: Error): void {
    const call = this.queuedCall;
    this.queuedCall = null;
    if (error) {
      call?.abort(error);
    }
  }

  private breadcrumbData() {
    return {
      isShutdown: this.isShutdown,
      isFetchingToken: this.isFetchingToken,
      callWaitingType: this.queuedCall?.type,
    };
  }
}
